import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { RootState } from '../../store';
import * as api from '../../services/api';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const VERBOSE = process.env.REACT_APP_VERBOSE === 'true';
function logVerbose(...args: unknown[]) {
  if (VERBOSE) console.log('[Admin visit-map]', ...args);
}

const GLOW_MIN = 10;
const GLOW_MAX = 60;
const CORE_MIN = 2;
const CORE_MAX = 8;

function glowRadius(count: number, maxCount: number): number {
  if (maxCount <= 0) return GLOW_MIN;
  return GLOW_MIN + (GLOW_MAX - GLOW_MIN) * Math.sqrt(count / maxCount);
}
function coreRadius(count: number, maxCount: number): number {
  if (maxCount <= 0) return CORE_MIN;
  return CORE_MIN + (CORE_MAX - CORE_MIN) * Math.sqrt(count / maxCount);
}
function dotColors(count: number, maxCount: number): { outer: string; mid: string; core: string } {
  if (maxCount <= 0) return { outer: '#00b4d8', mid: '#0077b6', core: '#caf0f8' };
  const t = count / maxCount;
  if (t > 0.7) return { outer: '#00e5ff', mid: '#00b4d8', core: '#ffffff' };
  if (t > 0.35) return { outer: '#48cae4', mid: '#0096c7', core: '#e0f7ff' };
  return { outer: '#90e0ef', mid: '#0077b6', core: '#caf0f8' };
}

/** Normalize API region to { lat, lon, count } (handles visit_count, lng, etc.). */
function normalizeRegion(r: Record<string, unknown>): { lat: number; lon: number; count: number } | null {
  const lat = Number(r.lat ?? r.latitude);
  const lon = Number(r.lon ?? r.lng ?? r.longitude);
  const count = Number(r.count ?? r.visit_count ?? 0);
  if (Number.isNaN(lat) || Number.isNaN(lon)) return null;
  return { lat, lon, count };
}

interface AdminUsersProps {
  embedded?: boolean;
}

const AdminUsers: React.FC<AdminUsersProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const isAllowedAdmin = useSelector((s: RootState) => s.user.isAllowedAdmin);
  const profile = useSelector((s: RootState) => s.user.profile);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingMfaId, setTogglingMfaId] = useState<number | null>(null);
  const [mapRegions, setMapRegions] = useState<Array<{ lat: number; lon: number; count: number }>>([]);
  const [stats, setStats] = useState<{
    signups_by_tier: Record<string, number>;
    total_signups: number;
    active_by_tier: Record<string, number>;
    groups: Array<{ name: string; tier: string; sub_accounts: number }>;
  } | null>(null);
  const [leadStats, setLeadStats] = useState<{
    total_leads: number;
    with_email: number;
    converted: number;
    conversion_rate: number;
    conversions_by_tier: Record<string, number>;
    specialty_breakdown: Record<string, number>;
  } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapSize, setMapSize] = useState({ width: 900, height: 400 });

  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    const updateSize = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setMapSize({ width: w, height: Math.round((w * 400) / 900) });
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!profile) {
      navigate('/login');
      return;
    }
    if (isAllowedAdmin !== true) {
      navigate('/');
      return;
    }
    api.adminUsersList()
      .then((res) => setUsers(res?.data?.users || []))
      .catch((e) => setErr(e?.message || 'Failed to load users'))
      .finally(() => setLoading(false));
    api.adminUserStats()
      .then((res) => res?.success && setStats(res.data))
      .catch(() => {});
    api.adminLeadStats()
      .then((res) => res?.success && setLeadStats(res.data))
      .catch(() => {});
    logVerbose('fetching visit-map (backend:', process.env.REACT_APP_BACKEND_URL, ')');
    api.adminVisitMap()
      .then((res) => {
        const raw = (res?.data?.regions ?? res?.regions ?? []) as Record<string, unknown>[];
        const regions = raw.map(normalizeRegion).filter((r): r is { lat: number; lon: number; count: number } => r !== null);
        logVerbose('visit-map success', { success: res?.success, rawLength: raw.length, normalizedLength: regions.length, sample: regions.slice(0, 3), fullResponse: res });
        setMapRegions(regions);
      })
      .catch((e: Error & { response?: Response; data?: unknown }) => {
        logVerbose('visit-map error', e?.message, { status: e?.response?.status, statusText: e?.response?.statusText, data: e?.data });
        setMapRegions([]);
      });
  }, [profile, isAllowedAdmin, navigate]);

  if (!profile || isAllowedAdmin !== true) return null;

  const maxCount = useMemo(() => Math.max(1, ...mapRegions.map((r) => r.count)), [mapRegions]);

  const handleResetPassword = (userId: number, email: string | null | undefined) => {
    const label = email?.trim() || `User #${userId}`;
    const newPw = window.prompt(`New password for ${label} (min 8 chars):`);
    if (!newPw) return;
    if (newPw.length < 8) { alert('Password must be at least 8 characters.'); return; }
    api.adminResetPassword(userId, newPw)
      .then((res) => {
        if (res?.success) alert(`Password reset for ${label}.`);
        else alert(res?.message || 'Reset failed.');
      })
      .catch((e: Error & { data?: { message?: string } }) => {
        alert(e?.data?.message || e?.message || 'Reset failed.');
      });
  };

  const handleToggleMfa = (userId: number, currentMfaRequired: boolean, email: string | null | undefined) => {
    const label = email?.trim() || `User ID ${userId}`;
    const next = !currentMfaRequired;
    const msg = next
      ? `Require MFA for "${label}"?`
      : `Allow "${label}" to log in without MFA?`;
    if (!window.confirm(msg)) return;
    setTogglingMfaId(userId);
    api.adminSetMfaRequired(userId, next)
      .then((res) => {
        if (res?.success) {
          setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, is_mfa_required: next } : u));
        } else {
          setErr(res?.message || 'Failed to update MFA setting');
        }
      })
      .catch((e: any) => setErr(e?.data?.message || e?.message || 'Failed to update MFA setting'))
      .finally(() => setTogglingMfaId(null));
  };

  const handleRemoveUser = (userId: number, email: string | null | undefined, username?: string) => {
    const label = email?.trim() || username?.trim() || `User ID ${userId}`;
    if (!window.confirm(`Remove user "${label}" (ID ${userId})? This cannot be undone.`)) return;
    setDeletingId(userId);
    setErr('');
    api
      .adminDeleteUser(userId)
      .then((res) => {
        if (res?.success) {
          setUsers((prev) => prev.filter((u) => u.id !== userId));
        } else {
          setErr(res?.message || 'Failed to remove user');
        }
      })
      .catch((e: Error & { data?: { message?: string } }) => {
        setErr(e?.data?.message || e?.message || 'Failed to remove user');
      })
      .finally(() => setDeletingId(null));
  };

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && (
        <p style={{ marginBottom: 16 }}>
          <Link to="/admin/users" style={{ fontWeight: 600, marginRight: 16 }}>Users</Link>
          <Link to="/admin/health" style={{ marginRight: 16 }}>Health</Link>
          <Link to="/admin/portal">Home</Link>
        </p>
      )}
      {!embedded && <h1>Admin – Users</h1>}

      {/* World map — dark network style: navy ocean, deep-blue land, glowing cyan dots */}
      <div style={{ marginBottom: 32, background: '#060d1a', borderRadius: 12, overflow: 'hidden', border: '1px solid #0a2040' }}>
        <div style={{ padding: '16px 20px 10px', display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e0f7ff' }}>Site visits by region</h2>
          <span style={{ fontSize: 12, color: '#2a6080' }}>drschmoe.ai — {mapRegions.length} regions</span>
        </div>
        <div ref={mapContainerRef} style={{ width: '100%', minHeight: 200 }}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 147 }}
            width={mapSize.width}
            height={mapSize.height}
            style={{ width: '100%', height: 'auto', display: 'block' }}
          >
            {/* Ocean background */}
            <rect width={mapSize.width} height={mapSize.height} fill="#060d1a" />

            {/* Subtle latitude grid lines */}
            {[-60, -30, 0, 30, 60].map(lat => (
              <line key={`lat-${lat}`}
                x1={0} y1={mapSize.height * (0.5 - lat / 180)}
                x2={mapSize.width} y2={mapSize.height * (0.5 - lat / 180)}
                stroke="#0a2040" strokeWidth={0.5} />
            ))}

            {/* Land: deep navy with visible teal-blue borders */}
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#0d1f35"
                    stroke="#1a4060"
                    strokeWidth={0.4}
                    style={{ outline: 'none' }}
                  />
                ))
              }
            </Geographies>

            {/* Glow gradients */}
            <defs>
              {mapRegions.map((r, i) => {
                const c = dotColors(r.count, maxCount);
                return (
                  <radialGradient key={`g-${i}`} id={`glow-${i}`} cx="50%" cy="50%" r="50%">
                    <stop offset="0%"   stopColor={c.outer} stopOpacity={0.95} />
                    <stop offset="25%"  stopColor={c.outer} stopOpacity={0.7} />
                    <stop offset="55%"  stopColor={c.mid}   stopOpacity={0.35} />
                    <stop offset="100%" stopColor={c.mid}   stopOpacity={0} />
                  </radialGradient>
                );
              })}
            </defs>

            {/* Glow blobs */}
            {mapRegions.map((r, i) => (
              <Marker key={`glow-${i}`} coordinates={[r.lon, r.lat]}>
                <circle r={glowRadius(r.count, maxCount)} fill={`url(#glow-${i})`} pointerEvents="none" />
              </Marker>
            ))}

            {/* Bright hot-spot core on top of each glow */}
            {mapRegions.map((r, i) => {
              const c = dotColors(r.count, maxCount);
              return (
                <Marker key={`core-${i}`} coordinates={[r.lon, r.lat]}>
                  <circle r={coreRadius(r.count, maxCount)} fill={c.core} opacity={0.95} pointerEvents="none" />
                </Marker>
              );
            })}
          </ComposableMap>
        </div>
        <div style={{ padding: '6px 20px 14px', display: 'flex', gap: 20 }}>
          <span style={{ fontSize: 11, color: '#1a5070' }}>● larger + brighter = more visits</span>
          <span style={{ fontSize: 11, color: '#1a5070' }}>● smaller + dimmer = fewer visits</span>
        </div>
      </div>

      {/* User tier statistics */}
      {stats && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ background: '#0d2a4a', borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(224,247,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Total Signups</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#e0f7ff', lineHeight: 1 }}>{stats.total_signups}</div>
              <div style={{ fontSize: 11, color: 'rgba(224,247,255,0.35)', marginTop: 4 }}>all time</div>
            </div>
            {(['Free', 'Individual Clinician', 'Small Business', 'Enterprise'] as const).map((tier) => (
              <div key={tier} style={{ background: '#0d1f35', border: '1px solid #1a4060', borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#4a8aba', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{tier}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#e0f7ff', lineHeight: 1 }}>{stats.signups_by_tier[tier] ?? 0}</div>
                <div style={{ fontSize: 11, color: '#2a6080', marginTop: 4 }}>{stats.active_by_tier[tier] ?? 0} active</div>
              </div>
            ))}
          </div>
          {stats.groups.length > 0 && (
            <div style={{ background: '#0d1f35', border: '1px solid #1a4060', borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#4a8aba', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Groups</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {stats.groups.map((g) => (
                  <div key={g.name} style={{ background: '#060d1a', border: '1px solid #1a4060', borderRadius: 8, padding: '8px 14px', minWidth: 180 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#e0f7ff' }}>{g.name}</div>
                    <div style={{ fontSize: 11, color: '#4a8aba', marginTop: 2 }}>{g.tier} &middot; {g.sub_accounts} sub-account{g.sub_accounts !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lead Library */}
      {leadStats && (
        <div style={{ marginBottom: 28, background: '#0a1a0a', border: '1px solid #1a4020', borderRadius: 12, padding: '18px 22px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#6fcf97', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Lead Library</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ background: '#0d2a14', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(111,207,151,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Total Leads</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#6fcf97', lineHeight: 1 }}>{leadStats.total_leads}</div>
            </div>
            <div style={{ background: '#0d2a14', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(111,207,151,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>With Email</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#6fcf97', lineHeight: 1 }}>{leadStats.with_email}</div>
              <div style={{ fontSize: 10, color: 'rgba(111,207,151,0.4)', marginTop: 3 }}>valid / accept_all</div>
            </div>
            <div style={{ background: '#0d2a14', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(111,207,151,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Converted</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#6fcf97', lineHeight: 1 }}>{leadStats.converted}</div>
              <div style={{ fontSize: 10, color: 'rgba(111,207,151,0.4)', marginTop: 3 }}>signed up users</div>
            </div>
            <div style={{ background: '#0d2a14', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(111,207,151,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Conversion Rate</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#6fcf97', lineHeight: 1 }}>{leadStats.conversion_rate}%</div>
              <div style={{ fontSize: 10, color: 'rgba(111,207,151,0.4)', marginTop: 3 }}>of total leads</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(111,207,151,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Conversions by Tier</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(['Free', 'Individual Clinician', 'Small Business', 'Enterprise'] as const).map((tier) => (
                  <div key={tier} style={{ background: '#060d0a', border: '1px solid #1a4020', borderRadius: 6, padding: '6px 12px', textAlign: 'center', minWidth: 90 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#6fcf97' }}>{leadStats.conversions_by_tier[tier] ?? 0}</div>
                    <div style={{ fontSize: 10, color: 'rgba(111,207,151,0.5)', marginTop: 2 }}>{tier}</div>
                  </div>
                ))}
              </div>
            </div>
            {Object.keys(leadStats.specialty_breakdown).length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(111,207,151,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Specialties</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {Object.entries(leadStats.specialty_breakdown).map(([spec, cnt]) => (
                    <span key={spec} style={{ background: '#060d0a', border: '1px solid #1a4020', borderRadius: 4, padding: '3px 8px', fontSize: 11, color: '#6fcf97' }}>
                      {spec}: {cnt}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {err && <div style={{ color: '#f66' }}>{err}</div>}
      {loading && <div>Loading...</div>}
      {!loading && !err && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>ID</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Email</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Name</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Group</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Date joined</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Tier</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Consent (version, time)</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>MFA</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Signup IP</th>
                <th style={{ border: '1px solid #ccc', padding: 8, textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>{u.id}</td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>{u.email?.trim() ? u.email : '—'}</td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>{u.first_name} {u.last_name}</td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>{(u as { group_name?: string | null }).group_name ?? 'N/A'}</td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>{u.date_joined || '-'}</td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>{u.tier_name ?? 'Unknown'}</td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>
                    {u.consent_status === 'signed'
                      ? `signed (v${(u as { consent_version?: string }).consent_version ?? '?'}, ${(u as { consent_timestamp?: string }).consent_timestamp ?? '?'})`
                      : (u.consent_status ?? 'none')}
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>
                    {u.mfa_enabled ? (u.mfa_setup_complete ? 'Yes' : 'Setup') : 'No'}
                    {(u as any).is_mfa_required === false ? ' (not required)' : ''}
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>{u.signup_ip || '-'}</td>
                  <td style={{ border: '1px solid #ccc', padding: 8 }}>
                    {(u as { is_allowed_admin?: boolean }).is_allowed_admin ? (
                      <span style={{ color: '#6b7280', fontSize: 12 }}>—</span>
                    ) : (
                      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => handleResetPassword(u.id, u.email)}
                          style={{ padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#1d4ed8' }}
                        >
                          Reset Pwd
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleMfa(u.id, (u as any).is_mfa_required !== false, u.email)}
                          disabled={togglingMfaId === u.id}
                          style={{
                            padding: '4px 9px',
                            fontSize: 11,
                            borderRadius: 4,
                            cursor: togglingMfaId === u.id ? 'wait' : 'pointer',
                            border: `1px solid ${(u as any).is_mfa_required !== false ? '#fca5a5' : '#86efac'}`,
                            background: (u as any).is_mfa_required !== false ? '#fee2e2' : '#dcfce7',
                            color: (u as any).is_mfa_required !== false ? '#b91c1c' : '#15803d',
                          }}
                        >
                          {togglingMfaId === u.id ? '…' : (u as any).is_mfa_required !== false ? 'MFA: On' : 'MFA: Off'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(u.id, u.email, (u as { username?: string }).username)}
                          disabled={deletingId === u.id}
                          style={{ padding: '4px 10px', fontSize: 12, cursor: deletingId === u.id ? 'wait' : 'pointer', color: '#c00' }}
                        >
                          {deletingId === u.id ? 'Removing...' : 'Remove'}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
